import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { startCheckout, fetchCurrentPrice, type CurrentPrice } from "@/lib/billing";

/** Fires the moment the 1-hour trial expires MID-SESSION: a blocking conversion popup over the
 *  desk (the feed keeps moving behind it — that's the point). Fresh page loads after expiry are
 *  routed to the gate screen by auth-context; this covers the live session. */
export function TrialGate() {
  const { me, refreshMe, signOut } = useAuth();
  const [expired, setExpired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState<CurrentPrice | null>(null);

  useEffect(() => {
    if (!expired) return;
    let alive = true;
    void fetchCurrentPrice().then((p) => { if (alive) setPrice(p); });
    return () => { alive = false; };
  }, [expired]);

  useEffect(() => {
    if (me?.trialEndsAt == null) return;
    const ms = me.trialEndsAt - Date.now();
    if (ms <= 0) { setExpired(true); return; }
    const t = setTimeout(() => setExpired(true), ms + 1000);
    return () => clearTimeout(t);
  }, [me?.trialEndsAt]);

  // Checkout runs in ANOTHER tab — poll the profile while the popup is up so the moment the
  // webhook flips the account active, this tab unlocks itself (no manual reload). Flipping
  // back to this window re-checks IMMEDIATELY: paying in the Stripe tab and clicking back
  // must open the desk, not show a stale paywall.
  useEffect(() => {
    if (!expired) return;
    const id = setInterval(() => void refreshMe(), 10_000);
    const onFocus = () => void refreshMe();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [expired, refreshMe]);

  if (!expired || me?.status === "active") return null;

  async function claim() {
    setBusy(true);
    const ok = await startCheckout();
    if (!ok) window.location.href = "mailto:info@earlywire.io?subject=Founding%20seat";
    setBusy(false);
  }

  return (
    <div className="fb-backdrop" style={{ zIndex: 80 }}>
      <div className="fb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fb-head"><span className="t">YOUR FREE HOUR IS UP</span></div>
        <div className="fb-body">
          <p className="fb-thanks">
            Don't want to miss the next big thing? Signals are still firing behind this window —
            you just can't act on them. <b>Current rate: ${price?.usd ?? 149}/month, locked for as
            long as you stay</b>{price?.listUsd ? ` (list $${price.listUsd})` : ""}.
            {price?.seatsRemaining != null && <> Only <b>{price.seatsRemaining} seats left</b> at this rate.</>}
          </p>
          <button className="auth-btn primary" disabled={busy} onClick={() => void claim()}>
            {busy ? "OPENING CHECKOUT…" : `CLAIM YOUR SEAT — $${price?.usd ?? 149}/MO`}
          </button>
          <button className="auth-btn" onClick={() => void signOut()}>SIGN OUT</button>
        </div>
      </div>
    </div>
  );
}
